import Axios, {CancelTokenSource} from 'axios';
import {DataProviderDataResponse, DATATABLE_SERVICE_INSTANCE} from '@/services/datatable/datatable.service';
import {SearchProvider} from '@/services/datatable/abstract-datatable-search-provider.service';

/**
 * Lazy Search provider is stupid and simply calls the API.
 */
class LazySearchProvider extends SearchProvider {
    source: CancelTokenSource = Axios.CancelToken.source();

    getInitialData(inputConfig: DisguiseDto.DTInputConfigurationDto): DataProviderDataResponse {
        return {
            data: this.initialTableData,
            page: this.initialTableData.lazyLoading?.page || 0,
            size: this.initialTableData.lazyLoading?.size || 10,
            maxSize: this.initialTableData.lazyLoading?.serverMaxSize || 0,
            totalPages: this.initialTableData.lazyLoading?.totalPages || 0,
        };
    }

    getData(inputConfig: DisguiseDto.DTInputConfigurationDto): Promise<DataProviderDataResponse> {
        this.source.cancel();
        this.source = Axios.CancelToken.source();

        return new Promise<DataProviderDataResponse>(((resolve, reject) => {
            DATATABLE_SERVICE_INSTANCE.loadTable(
                this.templateUrl,
                inputConfig,
                {cancelToken: this.source.token}).then((response) => {
                const mustResetPageIndex = this.mustResetPageIndex(response.data, inputConfig);
                if (mustResetPageIndex) {
                    // you requested a higher page index than available!
                    // redo request with page = 0;

                    const newInputConfig = {...inputConfig, page: 0};
                    this.getData(newInputConfig).then((newResponse) => {
                        resolve({
                            data: newResponse.data,
                            page: newResponse.data.lazyLoading?.page || 0,
                            size: newResponse.data.lazyLoading?.size || 10,
                            maxSize: newResponse.data.lazyLoading?.serverMaxSize || 0,
                            totalPages: newResponse.data.lazyLoading?.totalPages || 0,
                        });
                    }, (error) => {
                        reject(error);
                    });
                } else {
                    resolve({
                        data: response.data,
                        page: response.data.lazyLoading?.page || 0,
                        size: response.data.lazyLoading?.size || 10,
                        maxSize: response.data.lazyLoading?.serverMaxSize || 0,
                        totalPages: response.data.lazyLoading?.totalPages || 0,
                    });
                }
            }, (error) => {
                if (!error || !error.__CANCEL__) {
                    // request has been cancelled
                    reject(error);
                }
            });
        }));
    }

    /**
     * If the server has less pages than requested, this method returns true to indicate,
     * that a reload using page 0 must be done
     * @param response the API Response
     * @param inputConfig the original input configuration
     * @private true if reset must be scheduled
     */
    private mustResetPageIndex(
        response: DisguiseDto.DTTemplateDto,
        inputConfig: DisguiseDto.DTInputConfigurationDto
    ) {
        const serverTotalPages = response.lazyLoading?.totalPages;
        const requestedPageIndex = inputConfig.page;
        const serverHasResults = serverTotalPages !== undefined && serverTotalPages > 0;
        const serverHasLessPagesThanRequested = requestedPageIndex !== undefined
            && ((requestedPageIndex + 1) > (serverTotalPages || 0));
        return serverHasResults && serverHasLessPagesThanRequested;
    }
}

export {
    LazySearchProvider
};
